'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import { Link } from '@packages/modules/src/Link/Link';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import type { CategoryRecommendationFragmentFragmentDoc } from '@packages/gql/generated/shopping/CategoryRecommendationFragmentFragmentDoc';
import { Card } from '@packages/shared/src/ui/Card/Card';
import { Image } from '@packages/shared/src/ui/Image/Image';
import type { GTMEventGlycerinCategoryClick } from '@packages/tracking/src/types/events';

/* GraphQL */ `
  fragment CategoryRecommendationFragment on CategoryRecommendation {
    category {
      categoryId
      categoryLink
      name
      topsellerProduct {
        imageUrl
        productId
      }
    }
    tracking {
      token
    }
  }
`;

export type RecoCategoryCardProps = {
  /**
   * Position of category card in the recommendation list, starting with 1.
   */
  position: number;
  /**
   * Recommendation data for the category.
   */
  recommendationData: FragmentType<typeof CategoryRecommendationFragmentFragmentDoc>;
  /**
   * Recommendation type abbreviation, e.g. "prudsys_c2c"
   */
  rdeTypeAbbr?: string;
};

export const RecoCategoryCard = ({
  recommendationData,
  rdeTypeAbbr,
  position,
}: RecoCategoryCardProps) => {
  const dispatchGTMEvent = useTracking();

  const {
    category: { name, topsellerProduct, categoryLink, categoryId },
    tracking,
  } = unmask<typeof CategoryRecommendationFragmentFragmentDoc>(recommendationData);

  return (
    <li>
      <Card className="h-full w-full">
        <Link
          href={`${categoryLink}${categoryLink.endsWith('/') ? '' : '/'}?highlight=${
            topsellerProduct.productId
          }`}
          className="hover:text-color-primary no-underline hover:no-underline"
          onClick={() => {
            dispatchGTMEvent<GTMEventGlycerinCategoryClick>({
              event: 'CategoryClick',
              CategoryClickData: {
                category: 'category-click',
                categoryId,
                listPosition: position,
                recoId: rdeTypeAbbr,
                custom: {
                  trackingToken: tracking.token,
                },
              },
            });
          }}
        >
          <div className="flex flex-col justify-center">
            <div className="relative px-0.5 pt-0.5">
              <Image
                src={`${topsellerProduct.imageUrl}?$PL_square_hq$`}
                width={300}
                height={300}
                // TODO: add alt text to data structure
                alt={`Image of category: ${name}`}
              />
            </div>
            <div className="flex min-h-4.5 w-full items-center justify-center">
              <p title={name} className="text-size-body-sm px-1.5 text-center">
                {name}
              </p>
            </div>
          </div>
        </Link>
      </Card>
    </li>
  );
};
